import { envelopeMarkup } from '../envelope';
import { ball, body, coverBg, edge, fade, gloss, mix, n, rng, shade, shadowInk, star, tint, warm, type ArtPal } from './kit';

/**
 * Red Envelopes (抢红包): a round table seen from above with 8 seats; red envelopes fly out of the centre towards the
 * seats, the biggest one (the red envelope of the daily bonus — art/envelope.ts) under a gold crown (手气最佳 — drawn,
 * never written). Placeholder art (no text) until the final graphics — see docs/art-prompts.md (no glyphs, no 福,
 * no clocks, no green hats, no white/black mourning colours, no groups of four).
 */
export function hongbaoCover(P: ArtPal, p: string): string {
  const cx = 440;
  const cy = 208;
  const R = 112; // table top
  const SEATS = 8;
  const RS = 160; // seat ring
  const HERO = 1; // seat of the luckiest (top right)
  const top = P.dark ? mix(P.bg, P.red, 0.12) : tint(P.red, 0.9);
  const mid = P.dark ? mix(P.panel, P.gold, 0.06) : tint(P.gold, 0.9);
  const bg = coverBg(p, P, top, mid, P.bg, [cx, cy]);
  const red = P.red;
  const gold = warm(P);
  const lacquer = P.dark ? shade(red, 0.35) : red;
  const felt = P.dark ? mix(P.panel, red, 0.28) : tint(red, 0.78);
  const seatCols = [P.teal, P.blue, P.purple, P.orange, P.sky, P.pink, P.teal, P.gold];

  const angle = (i: number) => -Math.PI / 2 + (i / SEATS) * Math.PI * 2;

  // seats: round cushions with a gold rim, colour per seat (like the avatars at the table)
  let seats = '';
  for (let i = 0; i < SEATS; i++) {
    const a = angle(i);
    const x = cx + Math.cos(a) * RS;
    const y = cy + Math.sin(a) * RS;
    const c = seatCols[i] ?? P.teal;
    seats +=
      `<circle cx="${n(x + 2)}" cy="${n(y + 4)}" r="23" fill="${shadowInk(P)}" fill-opacity="${P.dark ? 0.4 : 0.12}"/>` +
      `<circle cx="${n(x)}" cy="${n(y)}" r="22" fill="${P.dark ? mix(P.panel, c, 0.35) : tint(c, 0.55)}" stroke="${i === HERO ? gold : edge(c, P)}" stroke-width="${i === HERO ? 3 : 2}"/>` +
      `<circle cx="${n(x)}" cy="${n(y)}" r="13" fill="${P.dark ? mix(P.panel, c, 0.6) : tint(c, 0.25)}"/>` +
      gloss(x - 8, y - 9, 6, 3, -35, 0.45);
  }

  // envelopes flying from the centre to the seats (not the hero seat — that one is drawn big below)
  const r = rng(88);
  let trails = '';
  let bodies = '';
  let borders = '';
  let flaps = '';
  let clasps = '';
  for (let i = 0; i < SEATS; i++) {
    if (i === HERO) continue;
    const a = angle(i);
    const t = 0.7 + r() * 0.14; // share of the way to the seat
    const x = cx + Math.cos(a) * RS * t;
    const y = cy + Math.sin(a) * RS * t;
    const x0 = cx + Math.cos(a) * 26;
    const y0 = cy + Math.sin(a) * 26;
    trails += `M${n(x0)} ${n(y0)}L${n(x - Math.cos(a) * 16)} ${n(y - Math.sin(a) * 16)}`;
    const rot = n(Math.cos(a) * 18 + (r() - 0.5) * 14); // leaning into the flight
    const w = 26;
    const h = 34;
    const ex = -w / 2;
    const ey = -h / 2;
    const g = (s: string) => `<g transform="translate(${n(x)} ${n(y)}) rotate(${rot})">${s}</g>`;
    bodies += g(`<rect x="${ex}" y="${ey}" width="${w}" height="${h}" rx="4"/>`);
    borders += g(`<rect x="${ex + 3}" y="${ey + 3}" width="${w - 6}" height="${h - 6}" rx="2"/>`);
    flaps += g(`<path d="M${ex + 1} ${ey + 2}L0 ${ey + 13}L${-ex - 1} ${ey + 2}"/>`);
    clasps += g(`<circle cx="0" cy="${ey + 13}" r="3.6"/>`);
  }
  const flying =
    `<path d="${trails}" fill="none" stroke="${gold}" stroke-opacity="${P.dark ? 0.35 : 0.5}" stroke-width="3" stroke-linecap="round" stroke-dasharray="2 7"/>` +
    `<g fill="url(#${p}ev)" stroke="${edge(red, P)}" stroke-width="1.5">${bodies}</g>` +
    `<g fill="none" stroke="${gold}" stroke-opacity=".6">${borders}</g>` +
    `<g fill="none" stroke="${shade(red, 0.3)}" stroke-width="1.4" stroke-linejoin="round">${flaps}</g>` +
    `<g fill="url(#${p}cl)" stroke="${edge(P.gold, P)}">${clasps}</g>`;

  // the luckiest envelope near the hero seat, with the crown above it and a few sparkles
  const ha = angle(HERO);
  const hx = cx + Math.cos(ha) * RS * 0.6;
  const hy = cy + Math.sin(ha) * RS * 0.6;
  const hs = 0.52; // 96×128 envelope → 50×67
  const hero = `<g transform="translate(${n(hx - 48 * hs)} ${n(hy - 70 * hs)}) rotate(8 ${n(48 * hs)} ${n(70 * hs)}) scale(${hs})">${envelopeMarkup('sealed', P, `${p}e`)}</g>`;
  // the crown sits on the luckiest seat
  const kx = cx + Math.cos(ha) * RS + 2;
  const ky = cy + Math.sin(ha) * RS - 36;
  // crown: three points with jewels (not four), a band with a row of dots
  const crown =
    `<g transform="translate(${n(kx)} ${n(ky)}) rotate(10)">` +
    `<ellipse cx="2" cy="16" rx="26" ry="5" fill="${shadowInk(P)}" fill-opacity="${P.dark ? 0.35 : 0.12}"/>` +
    `<path d="M-22 12L-25 -10L-11 1L0 -16L11 1L25 -10L22 12Z" fill="url(#${p}cr)" stroke="${edge(P.gold, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<rect x="-23" y="8" width="46" height="9" rx="3" fill="url(#${p}cr)" stroke="${edge(P.gold, P)}" stroke-width="2"/>` +
    `<circle cx="-25" cy="-11" r="3.6" fill="${red}"/><circle cx="0" cy="-17" r="4.2" fill="${red}"/><circle cx="25" cy="-11" r="3.6" fill="${red}"/>` +
    `<circle cx="-11" cy="12.5" r="1.8" fill="${shade(P.gold, 0.35)}"/><circle cx="0" cy="12.5" r="2.2" fill="${red}"/><circle cx="11" cy="12.5" r="1.8" fill="${shade(P.gold, 0.35)}"/>` +
    gloss(-12, -2, 5, 2.4, -40, 0.6) +
    `</g>`;
  let sparkles = '';
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + r() * 0.5;
    const d = 46 + r() * 22;
    sparkles += `<polygon points="${star(kx + Math.cos(a) * d, ky + 22 + Math.sin(a) * d * 0.8, 3 + r() * 4, 1.2 + r() * 0.8, 4)}" fill="${i % 2 ? gold : tint(gold, 0.35)}"/>`;
  }

  // table: lacquer rim, felt top, a gold ring and the glow of the pool in the middle
  const table =
    `<ellipse cx="${cx + 6}" cy="${cy + 14}" rx="${R + 8}" ry="${R + 4}" fill="url(#${p}sh)"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${R}" fill="url(#${p}rim)" stroke="${edge(lacquer, P)}" stroke-width="2"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${R - 14}" fill="url(#${p}top)" stroke="${shade(lacquer, 0.3)}" stroke-width="1.5"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${R - 22}" fill="none" stroke="${gold}" stroke-opacity=".55" stroke-width="1.5"/>` +
    `<circle cx="${cx}" cy="${cy}" r="46" fill="url(#${p}gw)"/>` +
    `<circle cx="${cx}" cy="${cy}" r="20" fill="url(#${p}cl)" stroke="${edge(P.gold, P)}" stroke-width="2"/>` +
    `<rect x="${cx - 7}" y="${cy - 7}" width="14" height="14" rx="2" fill="${shade(red, 0.2)}" stroke="${shade(P.gold, 0.35)}"/>` +
    gloss(cx - R * 0.5, cy - R * 0.55, 30, 10, -35, P.dark ? 0.16 : 0.35);

  const defs =
    `<defs>${bg.defs}` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.55 : 0.18) +
    body(`${p}rim`, lacquer, 0.35, 0.35, 0.6, 1) +
    body(`${p}top`, felt, 0.2, 0.25, 0.6, 1) +
    body(`${p}ev`, red, 0.3, 0.35, 0.6, 1) +
    ball(`${p}cl`, P.gold, 0.5, 0.35) +
    body(`${p}cr`, P.gold, 0.45, 0.3, 0.5, 1) +
    fade(`${p}gw`, gold, P.dark ? 0.5 : 0.55) +
    `</defs>`;

  return defs + bg.body + table + seats + flying + hero + crown + sparkles;
}
